import { useState, useRef, useEffect, useCallback } from 'react';
import { ChevronDown, ChevronRight, Star, MapPin, Loader2, Globe } from 'lucide-react';
import { LocationSearch, MAPBOX_TOKEN } from './LocationSearch';
import { LocationMap, LocationMapHandle } from './LocationMap';

interface Location {
  lat: number;
  lon: number;
  name: string;
}

interface LocationSectionProps {
  location: Location;
  onLocationChange: (location: Location) => void;
  favoriteLocations: Location[];
  addFavoriteLocation: (location: Location) => void;
  removeFavoriteLocation: (locationName: string) => void;
  isFavoriteLocation: (locationName: string) => boolean;
  loading?: boolean;
  expanded?: boolean;
  onExpandedChange?: (expanded: boolean) => void;
  showCustomCoordinates?: boolean;
  onShowCustomCoordinatesChange?: (show: boolean) => void;
  onCustomCoordinateSubmit?: (customLocation: Location) => void;
  pendingCustomLocation?: Location | null;
  onPendingCustomLocationHandled?: () => void;
}

// Utility function to extract city name from full location name
const getCityName = (fullName: string): string => {
  // Handle undefined/null names safely
  if (!fullName || typeof fullName !== 'string') {
    return 'Unknown Location';
  }
  // Split by comma and take the first part (city name)
  return fullName.split(',')[0].trim();
};

export function LocationSection({
  location,
  onLocationChange,
  favoriteLocations,
  addFavoriteLocation,
  removeFavoriteLocation,
  isFavoriteLocation,
  loading = false,
  expanded = false,
  onExpandedChange,
  onShowCustomCoordinatesChange,
  onCustomCoordinateSubmit,
  pendingCustomLocation,
  onPendingCustomLocationHandled,
}: LocationSectionProps) {
  const [recentSearches, setRecentSearches] = useState<Location[]>([]);
  const [isGettingLocation, setIsGettingLocation] = useState(false);
  const [locationError, setLocationError] = useState<string | null>(null);
  const mapRef = useRef<LocationMapHandle>(null);

  // Load recent searches from localStorage on mount
  useEffect(() => {
    const savedRecentSearches = localStorage.getItem('recentLocationSearches');
    if (savedRecentSearches) {
      try {
        setRecentSearches(JSON.parse(savedRecentSearches));
      } catch (error) {
        console.error('Error loading recent searches:', error);
      }
    }
  }, []);

  // Save recent searches to localStorage whenever it changes
  useEffect(() => {
    localStorage.setItem('recentLocationSearches', JSON.stringify(recentSearches));
    // Dispatch custom event to notify other components
    window.dispatchEvent(new CustomEvent('recentLocationSearchesUpdated'));
  }, [recentSearches]);

  const addToRecentSearches = useCallback((newLocation: Location) => {
    setRecentSearches((prev) => {
      // Remove if already exists to avoid duplicates
      const filtered = prev.filter((loc) => loc.name !== newLocation.name);
      // Add to beginning and limit to 10 recent searches
      return [newLocation, ...filtered].slice(0, 10);
    });
  }, []);

  const toggleFavorite = (loc: Location) => {
    if (isFavoriteLocation(loc.name)) {
      removeFavoriteLocation(loc.name);
    } else {
      addFavoriteLocation(loc);
    }
  };

  // Use favoriteLocations directly instead of filtering recent searches
  const favoriteLocationsList = favoriteLocations;
  const regularRecentSearches = recentSearches.filter((loc) => !isFavoriteLocation(loc.name));

  const LocationButton = ({ loc, isFavorite }: { loc: Location; isFavorite: boolean }) => (
    <div className="flex items-center">
      <button
        onClick={() => {
          if (!loading) {
            onLocationChange(loc);
            // Center map on selected location
            mapRef.current?.flyToLocation([loc.lon, loc.lat]);
          }
        }}
        className={`
          flex-1 flex items-center justify-between px-3 py-2 rounded-l text-sm transition-all duration-200
          ${
            location.name === loc.name
              ? 'text-pink-600 dark:text-pink-400 font-medium'
              : 'text-gray-700 dark:text-gray-300 hover:text-pink-600 dark:hover:text-pink-400'
          }
        `}
        disabled={loading}
      >
        <span>{getCityName(loc.name)}</span>
      </button>
      <button
        onClick={(e) => {
          e.stopPropagation();
          toggleFavorite(loc);
        }}
        className={`
          px-2 py-2 rounded-r transition-colors
          ${isFavorite ? 'text-pink-500 hover:text-pink-600' : 'text-gray-400 hover:text-pink-500'}
        `}
      >
        <Star className={`h-3 w-3 ${isFavorite ? 'fill-current' : ''}`} />
      </button>
    </div>
  );

  const handleLocationSelect = useCallback(
    (lat: number, lon: number, name: string) => {
      const newLocation = { lat, lon, name };
      onLocationChange(newLocation);
      addToRecentSearches(newLocation);
      // Center map on searched location
      mapRef.current?.flyToLocation([lon, lat]);
    },
    [onLocationChange, addToRecentSearches],
  );

  const handleMapLocationSelect = useCallback(
    (location: Location) => {
      handleLocationSelect(location.lat, location.lon, location.name);
      if (!isFavoriteLocation(location.name)) {
        addFavoriteLocation(location);
      }
      mapRef.current?.flyToLocation([location.lon, location.lat]);
    },
    [handleLocationSelect, isFavoriteLocation, addFavoriteLocation],
  );

  const handleCustomCoordinateSubmit = useCallback(
    (customLocation: Location) => {
      handleLocationSelect(customLocation.lat, customLocation.lon, customLocation.name);
      if (!isFavoriteLocation(customLocation.name)) {
        addFavoriteLocation(customLocation);
      }
      mapRef.current?.flyToLocation([customLocation.lon, customLocation.lat]);
      if (onShowCustomCoordinatesChange) {
        onShowCustomCoordinatesChange(false);
      }
    },
    [handleLocationSelect, isFavoriteLocation, addFavoriteLocation, onShowCustomCoordinatesChange],
  );

  // Handle pending custom location from ForecastPanel
  useEffect(() => {
    if (pendingCustomLocation) {
      const handler = onCustomCoordinateSubmit || handleCustomCoordinateSubmit;
      handler(pendingCustomLocation);
      if (onPendingCustomLocationHandled) {
        onPendingCustomLocationHandled();
      }
    }
  }, [
    pendingCustomLocation,
    onPendingCustomLocationHandled,
    handleCustomCoordinateSubmit,
    onCustomCoordinateSubmit,
  ]);

  const getCurrentLocation = async () => {
    setIsGettingLocation(true);
    setLocationError(null);

    try {
      // Check if geolocation is supported
      if (!navigator.geolocation) {
        throw new Error('Geolocation is not supported by this browser');
      }

      // Get current position
      const position = await new Promise<GeolocationPosition>((resolve, reject) => {
        navigator.geolocation.getCurrentPosition(resolve, reject, {
          enableHighAccuracy: true,
          timeout: 10000,
          maximumAge: 300000, // 5 minutes
        });
      });

      const { latitude, longitude } = position.coords;

      // Reverse geocode using Mapbox
      const response = await fetch(
        `https://api.mapbox.com/geocoding/v5/mapbox.places/${longitude},${latitude}.json?` +
          new URLSearchParams({
            access_token: MAPBOX_TOKEN,
            types: 'place,locality,neighborhood',
            limit: '1',
          }),
      );

      if (!response.ok) {
        throw new Error('Failed to get location name');
      }

      const data = await response.json();

      let locationName = 'Current Location';
      if (data.features && data.features.length > 0) {
        locationName = data.features[0].place_name || 'Current Location';
      }

      // Use the existing handleLocationSelect function
      handleLocationSelect(latitude, longitude, locationName);

      // Center map on current location
      mapRef.current?.flyToLocation([longitude, latitude]);
    } catch (error) {
      console.error('Error getting current location:', error);

      let errorMessage = 'Unable to get your location';
      if (error instanceof GeolocationPositionError) {
        switch (error.code) {
          case error.PERMISSION_DENIED:
            errorMessage = 'Location access denied. Please enable location permissions.';
            break;
          case error.POSITION_UNAVAILABLE:
            errorMessage = 'Location information unavailable.';
            break;
          case error.TIMEOUT:
            errorMessage = 'Location request timed out.';
            break;
        }
      } else if (error instanceof Error) {
        errorMessage = error.message;
      }

      setLocationError(errorMessage);

      // Clear error after 5 seconds
      setTimeout(() => setLocationError(null), 5000);
    } finally {
      setIsGettingLocation(false);
    }
  };

  return (
    <div>
      <button
        onClick={() => onExpandedChange && onExpandedChange(!expanded)}
        className="w-full flex items-center justify-between p-3 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700/50 transition-colors"
      >
        <div className="flex items-center gap-3">
          {expanded ? (
            <ChevronDown className="h-4 w-4 text-gray-500" />
          ) : (
            <ChevronRight className="h-4 w-4 text-gray-500" />
          )}
          <div className="text-left">
            <h4 className="text-sm font-medium text-gray-900 dark:text-gray-100">Location</h4>
            <p className="text-xs text-gray-500 dark:text-gray-400">{getCityName(location.name)}</p>
          </div>
        </div>
        <div className="flex items-center gap-2 text-xs text-gray-400">
          {favoriteLocations.length > 0 && (
            <span className="flex items-center gap-1">
              <Star className="h-3 w-3 text-pink-500 fill-current" />
              {favoriteLocations.length}
            </span>
          )}
          <span>{recentSearches.length} recent</span>
        </div>
      </button>

      {expanded && (
        <div className="mt-2 space-y-2 pl-7">
          {/* Search */}
          <div className="mb-3">
            <LocationSearch onLocationSelect={handleLocationSelect} />
          </div>

          {/* Map - Always visible */}
          <div className="mb-3">
            <LocationMap
              ref={mapRef}
              initialLocation={location}
              favoriteLocations={favoriteLocations}
              onLocationSelect={handleMapLocationSelect}
              className="h-64 rounded-lg border border-gray-200 dark:border-gray-700 overflow-hidden"
            />
          </div>

          {/* Current Location Button */}
          <div className="mb-3">
            <button
              onClick={getCurrentLocation}
              disabled={isGettingLocation || loading}
              className={`
                w-full flex items-center justify-center gap-2 px-3 py-2 rounded-lg text-sm font-medium transition-colors
                ${
                  isGettingLocation || loading
                    ? 'bg-gray-100 dark:bg-gray-700 text-gray-400 cursor-not-allowed'
                    : 'bg-pink-50 dark:bg-pink-900/20 text-pink-700 dark:text-pink-300 hover:bg-pink-100 dark:hover:bg-pink-900/30'
                }
              `}
            >
              {isGettingLocation ? (
                <>
                  <Loader2 className="h-4 w-4 animate-spin" />
                  Getting location...
                </>
              ) : (
                <>
                  <MapPin className="h-4 w-4" />
                  Use Current Location
                </>
              )}
            </button>

            {/* Error message */}
            {locationError && (
              <div className="mt-2 p-2 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg">
                <p className="text-xs text-red-600 dark:text-red-400">{locationError}</p>
              </div>
            )}
          </div>

          {/* Custom Coordinates Button */}
          <div className="mb-3">
            <button
              onClick={() => onShowCustomCoordinatesChange && onShowCustomCoordinatesChange(true)}
              disabled={loading}
              className={`
                w-full flex items-center justify-center gap-2 px-3 py-2 rounded-lg text-sm font-medium transition-colors
                ${
                  loading
                    ? 'bg-gray-100 dark:bg-gray-700 text-gray-400 cursor-not-allowed'
                    : 'bg-pink-50 dark:bg-pink-900/20 text-pink-700 dark:text-pink-300 hover:bg-pink-100 dark:hover:bg-pink-900/30'
                }
              `}
            >
              <Globe className="h-4 w-4" />
              Select custom coordinates
            </button>
          </div>

          {/* Favorite Locations */}
          {favoriteLocations.length > 0 && (
            <div className="mb-3">
              <div className="flex items-center space-x-2 mb-2">
                <Star className="h-3 w-3 text-pink-500 fill-current" />
                <span className="text-xs font-medium text-gray-700 dark:text-gray-300 uppercase tracking-wide">
                  Favorites
                </span>
              </div>
              <div className="space-y-1">
                {favoriteLocationsList.map((loc) => (
                  <LocationButton key={loc.name} loc={loc} isFavorite={true} />
                ))}
              </div>
            </div>
          )}

          {/* Recent Searches */}
          {regularRecentSearches.length > 0 && (
            <div>
              <div className="text-xs font-medium text-gray-700 dark:text-gray-300 mb-2 uppercase tracking-wide">
                Recent Searches
              </div>
              <div className="space-y-1 max-h-48 overflow-y-auto">
                {regularRecentSearches.map((loc) => (
                  <LocationButton key={loc.name} loc={loc} isFavorite={false} />
                ))}
              </div>
            </div>
          )}

          {/* Empty state when no recent searches */}
          {recentSearches.length === 0 && (
            <div className="text-center py-4 text-gray-500 dark:text-gray-400 text-sm">
              Search for locations to see them here
            </div>
          )}
        </div>
      )}
    </div>
  );
}
